import { ArrowLeft, Calendar, Clock, User, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import Footer from "@/components/Footer";
import BlogPostImage from "@/components/BlogPostImage";

const BlogPost3 = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Article */}
      <article className="py-12 md:py-20 px-4 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <Button asChild variant="ghost" className="mb-8 gap-2 hover:gap-3 transition-all">
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4" /> Voltar ao Blog
            </Link>
          </Button>

          <Card className="border-border/50 bg-card/50 backdrop-blur-sm p-8 md:p-12">
            {/* Featured Image */}
            <div className="aspect-video w-full rounded-lg mb-8 overflow-hidden">
              <BlogPostImage
                title="Segurança Digital: Proteja Seus Dados"
                backgroundImage="/assets/blog-seguranca-digital.webp"
                icon={<Shield className="w-full h-full" />}
              />
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>10 de Novembro, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>12 min de leitura</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Equipe Coddar</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Segurança Digital: Proteja Seus Dados
            </h1>

            {/* Content */}
            <div className="prose prose-invert max-w-none">
              <p className="text-lg text-muted-foreground mb-6">
                Em um mundo cada vez mais digital, a segurança de dados deixou de ser opcional. Ataques cibernéticos estão mais sofisticados, frequentes e custosos. Proteger seus dados não é apenas uma questão técnica - é uma necessidade de negócio.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">O Cenário Atual de Ameaças</h2>
              <p className="text-muted-foreground mb-4">
                As estatísticas são alarmantes:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Um ataque de ransomware ocorre a cada 11 segundos</li>
                <li>O custo médio de uma violação de dados é de US$ 4,45 milhões</li>
                <li>95% das violações de segurança são causadas por erro humano</li>
                <li>60% das pequenas empresas fecham em 6 meses após um ataque</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Principais Ameaças Digitais</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1. Ransomware</h3>
              <p className="text-muted-foreground mb-4">
                Malware que criptografa seus dados e exige resgate para liberá-los. Pode paralisar completamente uma empresa.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2. Phishing e Engenharia Social</h3>
              <p className="text-muted-foreground mb-4">
                Ataques que exploram o fator humano, enganando usuários para revelar informações sensíveis ou instalar malware.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3. Ataques DDoS</h3>
              <p className="text-muted-foreground mb-4">
                Sobrecarga de servidores com tráfego malicioso, tornando sistemas e sites inacessíveis.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4. Vazamento de Dados</h3>
              <p className="text-muted-foreground mb-4">
                Exposição não autorizada de informações confidenciais, causando danos à reputação e multas regulatórias.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Camadas de Proteção Essenciais</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">1. Backup e Disaster Recovery</h3>
              <p className="text-muted-foreground mb-4">
                A primeira linha de defesa contra perda de dados:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Backup automático em nuvem (regra 3-2-1)</li>
                <li>Testes regulares de restauração</li>
                <li>Versionamento de arquivos</li>
                <li>Replicação geográfica de dados</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">2. Antivírus e Anti-malware</h3>
              <p className="text-muted-foreground mb-4">
                Proteção em tempo real contra ameaças conhecidas e desconhecidas:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Detecção baseada em comportamento</li>
                <li>Proteção contra zero-day exploits</li>
                <li>Análise de URLs e downloads</li>
                <li>Quarentena automática de ameaças</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">3. Firewall e Segurança de Rede</h3>
              <p className="text-muted-foreground mb-4">
                Controle de tráfego e prevenção de intrusões:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Firewall de próxima geração (NGFW)</li>
                <li>Segmentação de rede</li>
                <li>VPN para acesso remoto seguro</li>
                <li>Monitoramento de tráfego 24/7</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4. Gestão de Identidade e Acesso</h3>
              <p className="text-muted-foreground mb-4">
                Controle rigoroso de quem acessa o quê:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Autenticação multifator (MFA)</li>
                <li>Princípio do menor privilégio</li>
                <li>Single Sign-On (SSO)</li>
                <li>Auditoria de acessos</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Soluções Acronis: Proteção Completa</h2>
              <p className="text-muted-foreground mb-4">
                Como parceiros Acronis, oferecemos soluções integradas de segurança cibernética:
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Acronis Cyber Protect Cloud</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Backup + antivírus + EDR em uma única solução</li>
                <li>Proteção contra ransomware com recuperação instantânea</li>
                <li>Gerenciamento centralizado de múltiplos dispositivos</li>
                <li>Conformidade com LGPD e outras regulamentações</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Acronis Advanced Security</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Detecção e resposta a ameaças (EDR)</li>
                <li>Proteção de e-mail contra phishing</li>
                <li>Análise forense de incidentes</li>
                <li>Inteligência de ameaças em tempo real</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Boas Práticas de Segurança</h2>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Para Empresas</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Implemente política de segurança clara</li>
                <li>Treine funcionários regularmente</li>
                <li>Mantenha sistemas sempre atualizados</li>
                <li>Realize auditorias de segurança periódicas</li>
                <li>Tenha um plano de resposta a incidentes</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">Para Usuários</h3>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Use senhas fortes e únicas</li>
                <li>Ative autenticação de dois fatores</li>
                <li>Desconfie de e-mails e links suspeitos</li>
                <li>Mantenha backups pessoais</li>
                <li>Use VPN em redes públicas</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Conformidade e Regulamentações</h2>
              <p className="text-muted-foreground mb-4">
                A LGPD (Lei Geral de Proteção de Dados) exige que empresas:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mb-6 space-y-2">
                <li>Protejam dados pessoais adequadamente</li>
                <li>Notifiquem incidentes de segurança</li>
                <li>Mantenham registros de tratamento de dados</li>
                <li>Implementem medidas técnicas e organizacionais</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                Multas podem chegar a 2% do faturamento, limitadas a R$ 50 milhões por infração.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">Conclusão</h2>
              <p className="text-muted-foreground mb-4">
                Segurança digital não é um produto - é um processo contínuo. Requer investimento, atenção e atualização constante. Mas o custo de não se proteger é infinitamente maior: perda de dados, paralisação de operações, danos à reputação e multas regulatórias.
              </p>
              <p className="text-muted-foreground">
                Na Coddar, oferecemos soluções completas de segurança digital, desde consultoria até implementação e monitoramento. Com as soluções Acronis e nossa expertise, sua empresa estará protegida contra as ameaças mais avançadas.
              </p>
            </div>

            {/* CTA */}
            <div className="mt-12 p-6 bg-primary/10 border border-primary/20 rounded-lg">
              <h3 className="text-xl font-bold text-foreground mb-2">Proteja sua empresa agora</h3>
              <p className="text-muted-foreground mb-4">
                Agende uma avaliação gratuita de segurança e descubra como podemos proteger seus dados e sistemas contra ameaças cibernéticas.
              </p>
              <Button asChild>
                <Link to="/seguranca-digital">Saiba Mais</Link>
              </Button>
            </div>
          </Card>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost3;
